import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    visitDate: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  // スクロール監視
  useState(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('phone', formData.phone);
      formBody.append('email', formData.email);
      formBody.append('visitDate', formData.visitDate);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d4lpmb77s74mkk8pdsm0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', phone: '', email: '', visitDate: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToForm = () => {
    document.getElementById('reservation-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* ナビゲーション */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className={`text-2xl font-bold transition-colors ${isScrolled ? 'text-gray-900' : 'text-white'}`} style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
              リビングプラス
            </div>
          </div>
          <div className="hidden md:flex items-center space-x-8">
            <a href="#gallery" className={`text-sm transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white hover:text-gray-200'}`}>モデルハウス</a>
            <a href="#floorplan" className={`text-sm transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white hover:text-gray-200'}`}>間取り</a>
            <a href="#recommend" className={`text-sm transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white hover:text-gray-200'}`}>おすすめポイント</a>
            <a href="#voice" className={`text-sm transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white hover:text-gray-200'}`}>お客様の声</a>
            <button onClick={scrollToForm} className="bg-teal-600 text-white px-6 py-2.5 rounded-full text-sm font-medium hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer">
              見学予約
            </button>
          </div>
        </div>
      </nav>

      {/* ヒーローセクション */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://static.readdy.ai/image/7755634a9ec0418b3b83e0cdd71f7c11/be58563679a6a2e324b5c08d09fd46e5.jpeg" 
            alt="リビングプラス モデルハウス外観"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 w-full">
          <div className="max-w-3xl mx-auto text-center">
            <div className="flex items-center justify-center space-x-4 mb-6">
              <span className="text-white text-sm font-light">創業1951年 | 75年の実績</span>
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                  <i className="ri-user-star-line text-white text-sm"></i>
                </div>
                <div className="w-8 h-8 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                  <i className="ri-home-smile-line text-white text-sm"></i>
                </div>
                <div className="w-8 h-8 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                  <i className="ri-shield-check-line text-white text-sm"></i>
                </div>
              </div>
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6" style={{ fontFamily: "'Noto Sans JP', sans-serif", lineHeight: '1.2' }}>
              価格そのままで、<br />
              <span className="text-teal-400">暮らしにゆとり</span>を<br />プラス
            </h1>
            
            <p className="text-sm md:text-xl text-white mb-8 font-light leading-relaxed" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
              ちょうどいい広さに、もうひと部屋をプラス<br />
              子育て家族に、もう少しのゆとりを。
            </p>
            
            <button onClick={scrollToForm} className="bg-white text-teal-700 px-10 py-4 rounded-full text-lg font-semibold hover:bg-gray-50 transition-all shadow-lg inline-flex items-center space-x-3 whitespace-nowrap cursor-pointer">
              <span>モデルハウス見学予約をする</span>
              <i className="ri-arrow-right-line"></i>
            </button>
          </div>
        </div>
      </section>

      {/* モデルハウスギャラリー */}
      <section id="gallery" className="py-24 bg-stone-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-16">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-light text-gray-900 mb-3" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
              湯沢市モデルハウス<br className="md:hidden" />ギャラリー
            </h2>
            <p className="text-gray-500 text-sm">外観・内装・設備の3つの魅力をご覧ください。</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow cursor-pointer">
              <div className="aspect-[4/3] overflow-hidden">
                <img 
                  src="https://static.readdy.ai/image/7755634a9ec0418b3b83e0cdd71f7c11/5ba88b5b49ad2ef32b3ee09428e389a8.jpeg"
                  alt="外観デザイン"
                  className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-6 text-center">
                <h3 className="text-xl font-bold text-gray-900 mb-2">外観デザイン</h3>
                <p className="text-gray-600 text-sm">ナチュラルで上質な佇まい</p>
              </div>
            </div>
            
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow cursor-pointer">
              <div className="aspect-[4/3] overflow-hidden">
                <img 
                  src="https://static.readdy.ai/image/7755634a9ec0418b3b83e0cdd71f7c11/960a3fdb6d0677947b14239c0b93b234.jpeg" 
                  alt="内装デザイン"
                  className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-6 text-center">
                <h3 className="text-xl font-bold text-gray-900 mb-2">内装デザイン</h3>
                <p className="text-gray-600 text-sm">家族が集まる心地よい空間</p>
              </div>
            </div>
            
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow cursor-pointer">
              <div className="aspect-[4/3] overflow-hidden">
                <img 
                  src="https://static.readdy.ai/image/7755634a9ec0418b3b83e0cdd71f7c11/8f75cb1caa533417a2c64e43a83fe958.jpeg" 
                  alt="充実の設備"
                  className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-6 text-center">
                <h3 className="text-xl font-bold text-gray-900 mb-2">充実の設備</h3>
                <p className="text-gray-600 text-sm">長く安心して暮らせる仕様</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 間取り図セクション */}
      <section id="floorplan" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6" style={{ fontFamily: "'Noto Sans JP', sans-serif", lineHeight: '1.2' }}>
              効率設計で<br />もう一部屋を実現
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto leading-relaxed">
              廊下や無駄なスペースを省き、同じ価格で部屋数を確保。<br />
              坪数を増やさず満足度をアップ。
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            <div className="bg-stone-50 rounded-2xl p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-3">参考プラン28坪　1階</h3>
              <p className="text-gray-600 text-sm mb-6">14.0坪</p>
              <div className="aspect-square bg-white rounded-xl p-6 flex items-center justify-center">
                <img 
                  src="https://static.readdy.ai/image/7755634a9ec0418b3b83e0cdd71f7c11/ee7fc42320bdd800ebd2a55ed3d3131c.png"
                  alt="1階プラン間取り図"
                  className="w-full h-full object-contain"
                />
              </div>
            </div>
            
            <div className="bg-stone-50 rounded-2xl p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-3">参考プラン28坪　2階</h3>
              <p className="text-gray-600 text-sm mb-6">14.0坪</p>
              <div className="aspect-square bg-white rounded-xl p-6 flex items-center justify-center">
                <img 
                  src="https://static.readdy.ai/image/7755634a9ec0418b3b83e0cdd71f7c11/df96a23582c89d514891c30f5a699760.png"
                  alt="2階プラン間取り図"
                  className="w-full h-full object-contain"
                />
              </div>
            </div>
          </div>

          <div className="bg-stone-50 rounded-2xl p-8 mb-20">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-left">
              <div>
                <p className="text-sm text-gray-600 mb-1">建物価格</p>
                <p className="text-2xl font-bold text-gray-900">1,650万円</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">月々</p>
                <p className="text-2xl font-bold text-gray-900">39,418円</p>
              </div>
            </div>
          </div>

          {/* 参考プラン */}
          <div className="mt-16">
            <div className="text-center mb-12">
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3 text-center" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                他にも複数プランを<br className="md:hidden" />ご用意しております。
              </h3>
              <p className="text-gray-600 text-sm text-center">ご家族のライフスタイルに合わせた様々なプランをご提案いたします。</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              <div className="bg-stone-50 rounded-2xl p-6">
                <h4 className="text-lg font-bold text-gray-900 mb-4">参考プラン24坪　1階</h4>
                <div className="aspect-square bg-white rounded-xl flex items-center justify-center">
                  <img 
                    src="https://static.readdy.ai/image/7755634a9ec0418b3b83e0cdd71f7c11/105f45fa3cf24e75312f524bd7ce81d6.png"
                    alt="参考プラン28坪 1階間取り図"
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>
              
              <div className="bg-stone-50 rounded-2xl p-6">
                <h4 className="text-lg font-bold text-gray-900 mb-4">参考プラン24坪　2階</h4>
                <div className="aspect-square bg-white rounded-xl flex items-center justify-center">
                  <img 
                    src="https://static.readdy.ai/image/7755634a9ec0418b3b83e0cdd71f7c11/69ad362b3de2f50088ce9d494f688998.png"
                    alt="参考プラン24坪 2階間取り図"
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>
            </div>

            <div className="bg-stone-50 rounded-2xl p-8 mb-12">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-left">
                <div>
                  <p className="text-sm text-gray-600 mb-1">建物価格</p>
                  <p className="text-2xl font-bold text-gray-900">1,540万円</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">月々</p>
                  <p className="text-2xl font-bold text-gray-900">36,790円</p>
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              <div className="bg-stone-50 rounded-2xl p-6">
                <h4 className="text-lg font-bold text-gray-900 mb-4">参考プラン21坪　1階</h4>
                <div className="aspect-square bg-white rounded-xl flex items-center justify-center">
                  <img 
                    src="https://static.readdy.ai/image/7755634a9ec0418b3b83e0cdd71f7c11/39dfb0d5752f899b2ff4bf185e3dc0d8.png"
                    alt="参考プラン21坪 1階間取り図"
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>
              
              <div className="bg-stone-50 rounded-2xl p-6">
                <h4 className="text-lg font-bold text-gray-900 mb-4">参考プラン21坪　2階</h4>
                <div className="aspect-square bg-white rounded-xl flex items-center justify-center">
                  <img 
                    src="https://static.readdy.ai/image/7755634a9ec0418b3b83e0cdd71f7c11/05271f700a6a8d5708f6b81b11854e41.png"
                    alt="参考プラン21坪 2階間取り図"
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>
            </div>

            <div className="bg-stone-50 rounded-2xl p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-left">
                <div>
                  <p className="text-sm text-gray-600 mb-1">建物価格</p>
                  <p className="text-2xl font-bold text-gray-900">1,430万円</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">月々</p>
                  <p className="text-2xl font-bold text-gray-900">34,162円</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* おすすめポイント */}
      <section id="recommend" className="py-24 bg-stone-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-24">
            <div className="text-center">
              <div className="inline-block bg-teal-100 text-teal-800 px-4 py-2 rounded-full text-sm font-medium mb-6">
                こんな方におすすめ
              </div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6" style={{ fontFamily: "'Noto Sans JP', sans-serif", lineHeight: '1.2' }}>
                限られた予算でも、<br />ゆとりをあきらめない
              </h2>
              <p className="text-gray-600 leading-relaxed max-w-2xl mx-auto">
                効率設計で価格そのまま、もう一部屋を確保。<br />
                来客対応や将来の変化にも柔軟に対応できる間取りです
              </p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 bg-teal-100 rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-home-heart-line text-teal-700 text-3xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                限られた予算の中でも、部屋数や暮らしのゆとりをあきらめたくない方
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                廊下や無駄なスペースを省いた効率設計で、価格そのままでも"もう一部屋"を確保したいご家族に最適です。
              </p>
            </div>
            
            <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 bg-teal-100 rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-door-open-line text-teal-700 text-3xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                来客対応やサポートに来てくれる方に、柔軟に使える部屋がほしい方
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                普段は子どもの遊び場、必要な時はゲストルームにも早変わり。将来の同居や在宅ワークにも使える多用途な間取りを求める方にぴったりです。
              </p>
            </div>
            
            <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 bg-teal-100 rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-building-line text-teal-700 text-3xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                アパート家賃がもったいない方におすすめ
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                同じ支払いで部屋数が多くて快適な暮らしになります。価格を抑えても外観・内装の質が高く、満足度の高い住まいを実現できます。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* お客様の声 */}
      <section id="voice" className="py-24 bg-stone-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-16 text-center">
            <div className="inline-block bg-rose-100 text-rose-800 px-4 py-2 rounded-full text-sm font-medium mb-6">
              お客様の声
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6" style={{ fontFamily: "'Noto Sans JP', sans-serif", lineHeight: '1.2' }}>
              実際に暮らす家族の、<br />リアルな満足の声
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="space-y-8">
              <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-lg transition-shadow">
                <div className="flex items-center space-x-4 mb-6">
                  <div className="w-14 h-14 rounded-full overflow-hidden flex-shrink-0">
                    <img 
                      src="https://public.readdy.ai/ai/img_res/2a00a542ff6a91cfaee50a536ca2d530.jpg"
                      alt="佐藤様"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div>
                    <p className="font-bold text-gray-900">佐藤様</p>
                    <p className="text-sm text-gray-500">秋田市・30代夫婦＋子ども1人</p>
                  </div>
                </div>
                <p className="text-gray-700 leading-relaxed">
                  「もう一部屋あるのは本当に助かります。普段は子どもの遊び場、来客のときはそのまま客間に。"部屋が足りない問題"が一気に解決しました。」
                </p>
              </div>
            </div>
            
            <div className="space-y-8">
              <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-lg transition-shadow">
                <div className="flex items-center space-x-4 mb-6">
                  <div className="w-14 h-14 rounded-full overflow-hidden flex-shrink-0">
                    <img 
                      src="https://readdy.ai/api/search-image?query=Professional%20upper%20body%20portrait%20photograph%20of%20cheerful%20Japanese%20woman%20in%20her%2030s%20with%20stylish%20bob%20haircut%20wearing%20chic%20French%20casual%20clothing%2C%20kind%20gentle%20smile%2C%20natural%20lighting%2C%20soft%20background%2C%20friendly%20welcoming%20personality%2C%20elegant%20relaxed%20style%2C%20authentic%20warm%20expression%2C%20looking%20at%20camera%2C%20high%20quality%20headshot&width=200&height=200&seq=customer2new2025&orientation=squarish"
                      alt="伊藤様"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div>
                    <p className="font-bold text-gray-900">伊藤様</p>
                    <p className="text-sm text-gray-500">湯沢市・30代夫婦</p>
                  </div>
                </div>
                <p className="text-gray-700 leading-relaxed">
                  「子どもが大きくなると部屋の使い方が変わりますが、1階の多用途ルームが受験のときの勉強部屋にも、親が来る時の寝室にも使えてとても便利。
将来の変化に対応できる家にして良かったです。」

                </p>
              </div>
            </div>
            
            <div className="space-y-8">
              <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-lg transition-shadow">
                <div className="flex items-center space-x-4 mb-6">
                  <div className="w-14 h-14 rounded-full overflow-hidden flex-shrink-0">
                    <img 
                      src="https://readdy.ai/api/search-image?query=Professional%20upper%20body%20portrait%20photograph%20of%20friendly%20smiling%20Japanese%20woman%20in%20her%2040s%20with%20shoulder%20length%20wavy%20hair%20wearing%20elegant%20formal%20dress%2C%20warm%20genuine%20smile%2C%20natural%20daylight%2C%20soft%20focus%20background%2C%20approachable%20welcoming%20personality%2C%20sophisticated%20graceful%20style%2C%20sincere%20happy%20expression%2C%20looking%20at%20camera%2C%20high%20quality%20headshot&width=200&height=200&seq=customer3new2025&orientation=squarish"
                      alt="渡辺様"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div>
                    <p className="font-bold text-gray-900">渡辺様</p>
                    <p className="text-sm text-gray-500">由利本荘市・40代夫婦＋子ども3人</p>
                  </div>
                </div>
                <p className="text-gray-700 leading-relaxed">
                  「5人家族なので部屋数が心配でしたが、効率的な間取りで全員が快適に過ごせています。子どもたちもそれぞれの空間を持てて満足しています。」
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Q&A */}
      <section id="qa" className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
              リビングプラス Q&A
            </h2>
          </div>
          
          <div className="space-y-8">
            <div>
              <h3 className="text-base font-bold text-gray-900 mb-3">
                Q1. 本当に"価格そのまま"で、もう一部屋つくれるんですか？
              </h3>
              <p className="text-sm text-gray-700 leading-relaxed pl-4">
                A1. はい、可能です。<br />
                廊下や無駄なスペースを見直した効率設計で、坪数を増やさずにもう一部屋を確保しています。
              </p>
            </div>
            
            <div>
              <h3 className="text-base font-bold text-gray-900 mb-3">
                Q2. 多用途の洋室は、どんな使い方ができますか？
              </h3>
              <p className="text-sm text-gray-700 leading-relaxed pl-4">
                A2. 子どもの遊び場、来客の寝室、在宅ワーク部屋、将来の同居スペースなど、家族構成や生活に合わせて自由に使えます。
              </p>
            </div>
            
            <div>
              <h3 className="text-base font-bold text-gray-900 mb-3">
                Q3. 価格を抑えても、断熱性や性能は大丈夫ですか？
              </h3>
              <p className="text-sm text-gray-700 leading-relaxed pl-4">
                A3. はい。専門資格者が設計・施工管理、第三者機関の検査も行っておりますので、断熱性・性能も安心。価格を抑えても品質はしっかり確保しています。性能面も安心していただけます。
              </p>
            </div>
            
            <div>
              <h3 className="text-base font-bold text-gray-900 mb-3">
                Q4. 見学してから具体的な間取りを相談できますか？
              </h3>
              <p className="text-sm text-gray-700 leading-relaxed pl-4">
                A4. はい、可能です。モデルハウスを見ながら、家族構成に合わせた提案を一緒に考えていきます。
              </p>
            </div>
            
            <div>
              <h3 className="text-base font-bold text-gray-900 mb-3">
                Q5. 多少のカスタマイズもできますか？
              </h3>
              <p className="text-sm text-gray-700 leading-relaxed pl-4">
                A5. はい。内装の色、収納量、設備など、暮らしやすさを基準にした調整ができます。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTAセクション */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://readdy.ai/api/search-image?query=Bright%20spacious%20modern%20Japanese%20living%20room%20interior%20with%20family%20gathering%2C%20natural%20light%20streaming%20through%20large%20windows%2C%20warm%20cozy%20atmosphere%2C%20simple%20clean%20background%2C%20professional%20real%20estate%20photography%2C%20inviting%20comfortable%20space&width=1920&height=800&seq=cta1&orientation=landscape" 
            alt="モデルハウス内観"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/50 to-black/30"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-xl md:text-3xl font-bold text-white mb-6 leading-tight" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
              今なら来場予約で、<br />クオカード500円プレゼント
            </h2>
          </div>
          
          <div className="max-w-lg mx-auto text-center">
            <p className="text-white text-lg mb-8 leading-relaxed font-light">
              まずは実際の空間を体感してください。<br />専門スタッフが丁寧にご案内します
            </p>
            <button onClick={scrollToForm} className="bg-white text-gray-900 px-10 py-4 rounded-full text-lg font-semibold hover:bg-gray-100 transition-all shadow-xl inline-flex items-center space-x-4 whitespace-nowrap cursor-pointer">
              <span>モデルハウス見学予約</span>
              <div className="w-10 h-10 bg-gray-900 rounded-full flex items-center justify-center">
                <i className="ri-arrow-right-line text-white"></i>
              </div>
            </button>
          </div>
        </div>
      </section>

      {/* 予約フォーム */}
      <section id="reservation-form" className="py-24 bg-white">
        <div className="max-w-3xl mx-auto px-6">
          
          <form onSubmit={handleSubmit} className="space-y-6" data-readdy-form id="model-house-form">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-900 mb-2">
                お名前 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all text-sm"
                placeholder="山田 太郎"
              />
            </div>
            
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-900 mb-2">
                電話番号 <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                required
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all text-sm"
                placeholder="090-1234-5678"
              />
            </div>
            
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-900 mb-2">
                メールアドレス <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all text-sm"
                placeholder="example@email.com"
              />
            </div>
            
            <div>
              <label htmlFor="visitDate" className="block text-sm font-medium text-gray-900 mb-2">
                見学予約希望日 <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                id="visitDate"
                name="visitDate"
                required
                value={formData.visitDate}
                onChange={(e) => setFormData({ ...formData, visitDate: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all text-sm"
              />
            </div>
            
            <div>
              <label htmlFor="message" className="block text-sm font-medium text-gray-900 mb-2">
                ご質問、ご要望
              </label>
              <textarea
                id="message"
                name="message"
                rows={5}
                maxLength={500}
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all resize-none text-sm"
                placeholder="ご質問やご要望がございましたらご記入ください（500文字以内）"
              />
              <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500文字</p>
            </div>
            
            {submitStatus === 'success' && (
              <div className="bg-teal-50 border border-teal-200 text-teal-800 px-4 py-3 rounded-lg text-sm">
                予約を受け付けました。担当者より折り返しご連絡いたします。
              </div>
            )}
            
            {submitStatus === 'error' && (
              <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
                送信に失敗しました。もう一度お試しください。
              </div>
            )}
            
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-teal-700 text-white py-4 rounded-full text-lg font-semibold hover:bg-teal-800 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
            >
              {isSubmitting ? '送信中...' : '予約内容を送信する'}
            </button>
          </form>
        </div>
      </section>

      {/* 会社紹介・アクセス */}
      <section className="py-24 bg-stone-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                運営会社
              </h2>
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-semibold text-gray-500 mb-1">会社名</p>
                  <p className="text-gray-900 font-medium">湯沢開発株式会社</p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-500 mb-1">代表者</p>
                  <p className="text-gray-900">高橋　正明</p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-500 mb-1">所在地</p>
                  <p className="text-gray-900">〒012-0844　秋田県湯沢市田町2-1-3</p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-500 mb-1">電話番号</p>
                  <p className="text-gray-900">0183-73-7125</p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-500 mb-1">定休日</p>
                  <p className="text-gray-900">水曜日</p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-500 mb-1">創業</p>
                  <p className="text-gray-900">1951年</p>
                </div>
                <div className="pt-4 border-t border-gray-200">
                  <p className="text-xs text-gray-500 mb-1">宅地建物取引業免許</p>
                  <p className="text-xs text-gray-700">秋田県知事免許(17)第43号</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">建設業登録</p>
                  <p className="text-xs text-gray-700">秋田県知事許可（般-4）第80084号</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">一級建築士事務所</p>
                  <p className="text-xs text-gray-700">秋田県知事登録　第16-110A-0250　号</p>
                </div>
              </div>
            </div>
            
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                アクセス
              </h2>
              <div className="rounded-2xl overflow-hidden shadow-lg h-96">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2995.8!2d140.500223!3d39.188666!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMznCsDExJzE5LjIiTiAxNDDCsDMwJzAwLjgiRQ!5e0!3m2!1sja!2sjp!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="湯沢開発株式会社 地図"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* フッター */}
      <footer className="bg-slate-800 text-white py-16 mx-6 mb-6 rounded-3xl relative overflow-hidden">
        <div className="absolute bottom-0 left-0 right-0 text-[120px] font-bold text-white/5 text-center leading-none pb-4" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
          YUZAWA KAIHATSU
        </div>
        
        <div className="max-w-7xl mx-auto px-8 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
            <div>
              <a href="https://www.y-kaihatsu.co.jp/" target="_blank" rel="noopener noreferrer" className="text-2xl font-bold mb-6 hover:text-teal-400 transition-colors cursor-pointer inline-block" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                湯沢開発株式会社
              </a>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">サービス</h4>
              <ul className="space-y-2 text-sm text-gray-300">
                <li><a href="#gallery" className="hover:text-white transition-colors cursor-pointer">モデルハウス</a></li>
                <li><a href="#floorplan" className="hover:text-white transition-colors cursor-pointer">施工事例</a></li>
                <li><a href="#recommend" className="hover:text-white transition-colors cursor-pointer">会社概要</a></li>
                <li><a href="#reservation-form" className="hover:text-white transition-colors cursor-pointer">お問い合わせ</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">プライバシーポリシー</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">フォローする</h4>
              <ul className="space-y-2 text-sm text-gray-300">
                <li className="flex items-center space-x-2">
                  <i className="ri-instagram-line"></i>
                  <a href="https://www.instagram.com/machapacha7125/" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors cursor-pointer">Instagram</a>
                </li>
                <li className="flex items-center space-x-2">
                  <i className="ri-youtube-line"></i>
                  <a href="http://www.youtube.com/@yuzawakaihatsu" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors cursor-pointer">YouTube</a>
                </li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">最新情報をお届けします</h4>
              <div className="flex items-center space-x-2">
                <input
                  type="email"
                  placeholder="メールアドレス"
                  className="flex-1 bg-slate-700 border border-slate-600 px-4 py-2.5 rounded-full text-sm focus:outline-none focus:border-teal-500 transition-colors"
                />
                <button className="w-10 h-10 bg-white rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors flex-shrink-0 cursor-pointer">
                  <i className="ri-arrow-right-line text-slate-800"></i>
                </button>
              </div>
            </div>
          </div>
          
          <div className="mt-12 pt-8 border-t border-slate-700 flex flex-col md:flex-row justify-between items-center text-sm text-gray-400">
            <p>Copyright © 2025 湯沢開発株式会社. All rights reserved.</p>
            <a href="https://readdy.ai/?origin=logo" target="_blank" rel="noopener noreferrer" className="mt-4 md:mt-0 hover:text-white transition-colors cursor-pointer">
              Website Builder
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
